import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../Common/Configurations/AuthContext";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import {
  RiArrowLeftLine,
  RiMoneyRupeeCircleLine,
  RiCheckboxCircleLine,
  RiErrorWarningLine,
  RiBusLine,
  RiUserLine,
  RiMapPinLine,
} from "react-icons/ri";

const BusPayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const {
    bus,
    searchQuery,
    selectedSeats,
    passengerCount,
    passengerDetails,
    selectedBoardingPoint,
    selectedDroppingPoint,
    totalAmount = 0,
    baseFare = 0,
    convenienceFee = 0,
  } = location.state || {};

  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Format date to "09-Dec-2025"
  const formatDate = (dateString) => {
    if (!dateString) return "";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      const day = date.getDate().toString().padStart(2, "0");
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    } catch (error) {
      console.error("Date formatting error:", error);
      return dateString;
    }
  };

  // Redirect if no booking data
  if (!bus || !selectedSeats || !passengerDetails) {
    navigate("/bus/results");
    return null;
  }

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `INR${Math.round(num).toLocaleString("en-IN")}`;
  };

  const calculateBreakdown = () => {
    if (!selectedSeats) return { baseFare: 0, convenienceFee: 0, total: 0 };

    const base = selectedSeats.reduce(
      (sum, seat) => sum + (parseFloat(seat.totalPriceWithTaxes) || 0),
      0
    );
    const convenience = selectedSeats.reduce(
      (sum, seat) => sum + (parseFloat(seat.convenienceFee) || 100),
      0
    );
    const total = base + convenience;

    return {
      baseFare: base,
      convenienceFee: convenience,
      total,
    };
  };

  // Load Razorpay script
  function loadScript(src) {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }

  // Block seats function - called after payment success
  const bookSeats = async (paymentResponse) => {
    try {
      const bookRequest = {
        CompanyId: user?.cid || "00000000-0000-0000-0000-000000000000",
        UserId: user?.id || "00000000-0000-0000-0000-000000000000",
        SupplierId: bus.supplierId,  
        SourceCity: searchQuery.fromStation,
        DestinationCity: searchQuery.toStation,
        InventoryType: bus.inventoryType,
        RouteScheduleId: bus.routeScheduleId,
        CustomerName: `${passengerDetails[0].firstName} ${passengerDetails[0].lastName}`,
        CustomerLastName: passengerDetails[0].lastName || "",
        CustomerEmail: passengerDetails[0].email,
        DOJ: new Date(searchQuery.travelDate),
        BoardingPoint: {
          Id: selectedBoardingPoint.id,
          Location: selectedBoardingPoint.location,
          Time: selectedBoardingPoint.time,
        },
        DroppingPoint: {
          Id: selectedDroppingPoint.id,
          Location: selectedDroppingPoint.location,
          Time: selectedDroppingPoint.time,
        },
        CustomerPhone: passengerDetails[0].phone,
        EmergencyPhNumber: passengerDetails[0].phone,
        CustomerAddress: "Not provided",
        BookingStatus: "BOOKED", // Direct BOOKED status
        PaymentStatus: "SUCCESS", // Payment already successful
        PaymentReference: {
          paymentId: paymentResponse.razorpay_payment_id,
          orderId: paymentResponse.razorpay_order_id,
          signature: paymentResponse.razorpay_signature,
        },
        BookSeatPaxDetails: selectedSeats.map((seat, index) => {
          const passenger = passengerDetails[index] || passengerDetails[0];
          return {
            Age: passenger.age,
            Name: `${passenger.firstName} ${passenger.lastName}`,
            SeatNbr: seat.id,
            Sex:
              passenger.title === "Ms" || passenger.title === "Mrs" ? "F" : "M",
            Fare: parseFloat(seat.totalPriceWithTaxes) || 0,
            ServiceTaxAmount: seat.serviceTaxAmount || 0,
            OperatorServiceChargeAbsolute:
              seat.operatorServiceChargeAbsolute || 0,
            TotalFareWithTaxes: parseFloat(seat.totalPriceWithTaxes) || 0,
            LadiesSeat: seat.ladiesSeat || false,
            LastName: passenger.lastName || "",
            Mobile: passenger.phone,
            Title: passenger.title,
            Email: passenger.email,
            IdType: passenger.idProof,
            IdNumber: passenger.idProofNumber || "",
            NameOnId: `${passenger.firstName} ${passenger.lastName}`,
            Primary: index === 0,
            Ac: seat.ac || false,
            Sleeper: seat.sleeper || false,
          };
        }),
      };

      const bookResponse = await apiService.post(
        config.BOOKBUSSEATS,
        bookRequest
      );

      if (
        bookResponse &&
        bookResponse.apiStatus &&
        bookResponse.apiStatus.success
      ) {
        return {
          success: true,
          ticketKey: bookResponse.bookTicketKey,
          bookingId: bookResponse.bookingId || `BUS${Date.now()}`,
        };
      } else {
        return {
          success: false,
          error: bookResponse?.apiStatus?.message || "Failed to book seats",
        };
      }
    } catch (error) {
      console.error("Book seats error:", error);
      return {
        success: false,
        error: error.message || "Failed to book seats due to network error",
      };
    }
  };

  // Function to navigate to confirmation screen with all data
  const navigateToConfirmation = (status, bookingData = {}) => {
    const breakdown = calculateBreakdown();

    const confirmationData = {
      bus,
      searchQuery,
      selectedSeats,
      passengerCount,
      passengerDetails,
      selectedBoardingPoint,
      selectedDroppingPoint,
      totalAmount: breakdown.total,
      baseFare: breakdown.baseFare,
      convenienceFee: breakdown.convenienceFee,
      bookingStatus: status,
      ...bookingData,
    };

    navigate("/bus/booking-confirmation", {
      state: confirmationData,
    });
  };

  // Razorpay payment handler
  async function displayRazorpay() {
    const res = await loadScript(
      "https://checkout.razorpay.com/v1/checkout.js"
    );

    if (!res) {
      alert("Razorpay SDK failed to load. Are you online?");
      return;
    }

    try {
      const breakdown = calculateBreakdown();
      const finalAmount = breakdown.total * 100;

      // 1. MAKEPAYMENT API call
      const result = await apiService.post(
        `${config.MAKEPAYMENT}?Amount=${breakdown.total}&PayRequest=RAZORPAY&CompanyId=${user?.cid}`
      );

      if (!result.succeeded) {
        alert("Payment initiation failed. Please try again.");
        return;
      }

      const { amount, id: order_id, currency, razorpayKey, name } = result.data;

      const options = {
        key: razorpayKey,
        amount: finalAmount,
        currency: currency,
        name: name,
        description: `Bus Booking - ${bus.operatorName} | ${searchQuery.fromStation} to ${searchQuery.toStation}`,
        order_id: order_id,
        handler: async function (response) {
          try {
            // 2. BOOKBUSSEATS API call after payment
            const bookingResult = await bookSeats(response);

            if (bookingResult.success) {
              // Navigate to confirmation with SUCCESS status
              navigateToConfirmation("CONFIRMED", {
                bookingId: bookingResult.bookingId,
                ticketKey: bookingResult.ticketKey,
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                message: "Payment successful and booking confirmed!",
                showSuccessIcon: true,
              });
            } else {
              // Payment successful but booking failed
              navigateToConfirmation("PAYMENT_SUCCESS_BOOKING_FAILED", {
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                amount: breakdown.total,
                errorMessage: `Payment successful but booking failed: ${bookingResult.error}. Please contact support.`,
                showContactSupport: true,
              });
            }
          } catch (error) {
            console.error("Payment handler error:", error);
            // Payment successful but something went wrong
            navigateToConfirmation("PAYMENT_SUCCESS_BOOKING_PENDING", {
              paymentId: response.razorpay_payment_id,
              orderId: response.razorpay_order_id,
              amount: breakdown.total,
              message:
                "Payment successful! Please note your payment ID for reference.",
              showSuccessIcon: true,
            });
          }
        },
        prefill: {
          email: user?.email || passengerDetails[0]?.email,
          contact: user?.phoneNumber || passengerDetails[0]?.phone,
          name: `${passengerDetails[0]?.firstName} ${passengerDetails[0]?.lastName}`,
        },
        notes: {
          bookingType: "BUS",
          fromStation: searchQuery.fromStation,
          toStation: searchQuery.toStation,
          travelDate: formatDate(searchQuery.travelDate),
          passengerCount: passengerCount,
          seats: selectedSeats.map((s) => s.id).join(","),
        },
        theme: {
          color: "#3399cc",
        },
      };

      const paymentObject = new window.Razorpay(options);

      paymentObject.on("payment.failed", function (response) {
        console.error("Payment failed:", response.error);
        setIsProcessingPayment(false);
        navigateToConfirmation("PAYMENT_FAILED", {
          errorMessage: `Payment failed: ${response.error.description}. Please try again.`,
          errorCode: response.error.code,
          errorSource: response.error.source,
        });
      });

      paymentObject.on("payment.cancel", function (response) {
        console.log("Payment cancelled by user:", response);
        setIsProcessingPayment(false);
        navigateToConfirmation("PAYMENT_CANCELLED", {
          message:
            "Payment was cancelled. Please try again if you wish to proceed.",
        });
      });

      paymentObject.open();
    } catch (error) {
      console.error("Payment error:", error);
      alert("Payment initiation failed. Please try again.");
      setIsProcessingPayment(false);
    }
  }

  const handlePayment = async () => {
    setIsProcessingPayment(true);
    try {
      await displayRazorpay();
    } catch (error) {
      console.error("Payment error:", error);
      alert("Payment failed. Please try again.");
      setIsProcessingPayment(false);
    }
  };

  const handleBackToSeats = () => {
    navigate(-1);
  };

  const breakdown = calculateBreakdown();

  return (
    <div id="layout-wrapper">
      <Header />

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="payment-section">
              <div className="card border-primary">
                <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                  <h4 className="mb-0" style={{ color: "white" }}>
                    <RiMoneyRupeeCircleLine className="me-2" />
                    Complete Payment
                  </h4>
                  <button
                    className="btn btn-light btn-sm"
                    onClick={handleBackToSeats}
                  >
                    <RiArrowLeftLine className="me-1" />
                    Back to Seats
                  </button>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-8">
                      <h6>Booking Summary</h6>
                      <div className="row">
                        <div className="col-md-6">
                          <p>
                            <strong>Route:</strong> {searchQuery.fromStation} →{" "}
                            {searchQuery.toStation}
                          </p>
                          <p>
                            <strong>Date:</strong>{" "}
                            {formatDate(searchQuery.travelDate)}
                          </p>
                          <p>
                            <strong>Time:</strong> {bus.departureTime} -{" "}
                            {bus.arrivalTime}
                          </p>
                          <p>
                            <strong>Operator:</strong> {bus.operatorName}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <p>
                            <strong>Passengers:</strong> {passengerCount}
                          </p>
                          <p>
                            <strong>Boarding Point:</strong>{" "}
                            {selectedBoardingPoint?.location} at{" "}
                            {selectedBoardingPoint?.time}
                          </p>
                          <p>
                            <strong>Dropping Point:</strong>{" "}
                            {selectedDroppingPoint?.location} at{" "}
                            {selectedDroppingPoint?.time}
                          </p>
                        </div>
                      </div>

                      <div className="mt-3">
                        <h6>Selected Seats & Passengers</h6>
                        <div className="table-responsive">
                          <table className="table table-bordered table-sm">
                            <thead>
                              <tr>
                                <th>Seat</th>
                                <th>Passenger</th>
                                <th>Age</th>
                                <th>ID Proof</th>
                                <th>Bus Fare</th>
                                <th>Conv. Fee</th>
                                <th>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedSeats.map((seat, index) => {
                                const seatBaseFare =
                                  seat.totalPriceWithTaxes || 0;
                                const seatConvFee = seat.convenienceFee || 100;
                                const seatTotal = seatBaseFare + seatConvFee;

                                return (
                                  <tr key={seat.id}>
                                    <td>
                                      <strong>{seat.id}</strong>
                                    </td>
                                    <td>
                                      {passengerDetails[index]?.title}{" "}
                                      {passengerDetails[index]?.firstName}{" "}
                                      {passengerDetails[index]?.lastName}
                                    </td>
                                    <td>
                                      {passengerDetails[index]?.age} years
                                    </td>
                                    <td>{passengerDetails[index]?.idProof}</td>
                                    <td>{formatCurrency(seatBaseFare)}</td>
                                    <td>{formatCurrency(seatConvFee)}</td>
                                    <td>
                                      <strong>
                                        {formatCurrency(seatTotal)}
                                      </strong>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="card bg-light">
                        <div className="card-header bg-success text-white">
                          <h6 className="mb-0">Payment Details</h6>
                        </div>
                        <div className="card-body">
                          <div className="d-flex justify-content-between mb-2">
                            <div>
                              <span>Bus Fare (Includes taxes)</span>
                              <div className="small text-muted">
                                For {passengerCount} passenger(s)
                              </div>
                            </div>
                            <span>
                              {formatCurrency(baseFare || breakdown.baseFare)}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <div>
                              <span>Convenience Fee</span>
                              <div className="small text-muted">
                                (INR100 per seat × {passengerCount})
                              </div>
                            </div>
                            <span>
                              {formatCurrency(
                                convenienceFee || breakdown.convenienceFee
                              )}
                            </span>
                          </div>
                          <hr />
                          <div className="d-flex justify-content-between mb-3">
                            <strong>Total Amount to Pay:</strong>
                            <strong>
                              {formatCurrency(totalAmount || breakdown.total)}
                            </strong>
                          </div>

                          <button
                            className="btn btn-success w-100"
                            onClick={handlePayment}
                            disabled={isProcessingPayment}
                          >
                            {isProcessingPayment ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Opening Payment...
                              </>
                            ) : (
                              `Pay ${formatCurrency(
                                totalAmount || breakdown.total
                              )}`
                            )}
                          </button>

                          <div className="mt-2 text-center small text-muted">
                            <RiCheckboxCircleLine className="me-1" />
                            Seats will be booked immediately after payment
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusPayment;
